"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { BarChart } from "@/components/tools/stats-distributions/charts";
import {
  DistributionTable,
  type MeaningLabels,
  ProbabilitySummary,
} from "@/components/tools/stats-distributions/discrete-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { barGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import {
  type DiscreteModel,
  discreteProbabilities,
  distributionTable,
  tableRange,
} from "@/lib/tool-kit/stats-distributions/discrete";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  type GeometricConvention,
  geometricFirst,
  geometricModel,
  geometricQuantile,
  toRFailures,
} from "@/lib/tool-kit/stats-distributions-more/geometric";

/** English strings. A translated content file overrides these through `ui`. */
export const GEOMETRIC_DISTRIBUTION_CALCULATOR_STRINGS = {
  conventionLabel: "What are you counting?",
  conventionTrials:
    "Trials up to and including the first success (X = 1, 2, 3, …)",
  conventionFailures:
    "Failures before the first success (Y = 0, 1, 2, …) — R's dgeom",
  pLabel: "Probability of success on each trial (p)",
  kTrialsLabel: "Trial on which the first success happens (k)",
  kFailuresLabel: "Number of failures before the first success (k)",
  k2Label: "Upper end of a range (optional)",
  k2Hint: "Adds the probability of a value from k up to this number.",
  results: "Results",
  probability: "Probability",
  meaning: "Meaning",
  value: "Value",
  trialsEqual: "first success on trial {k}",
  trialsAtMost: "first success on trial {k} or earlier",
  trialsLessThan: "first success before trial {k}",
  trialsAtLeast: "first success on trial {k} or later",
  trialsGreaterThan: "first success after trial {k}",
  trialsBetween: "first success between trials {a} and {b}, inclusive",
  failuresEqual: "failure count is exactly {k}",
  failuresAtMost: "failure count is at most {k}",
  failuresLessThan: "failure count is below {k}",
  failuresAtLeast: "failure count is at least {k}",
  failuresGreaterThan: "failure count is above {k}",
  failuresBetween: "failure count from {a} to {b}, inclusive",
  meanTrials: "Mean (1/p)",
  meanFailures: "Mean ((1 − p)/p)",
  variance: "Variance ((1 − p)/p²)",
  sd: "Standard deviation",
  median: "Median",
  workingHeading: "How this was calculated",
  pmfTrials:
    "P(X = k) = (1 − p)^(k − 1) × p = (1 − {p})^({k} − 1) × {p} = {value}",
  cdfTrials: "P(X ≤ k) = 1 − (1 − p)^k = 1 − (1 − {p})^{k} = {value}",
  pmfFailures: "P(Y = k) = (1 − p)^k × p = (1 − {p})^{k} × {p} = {value}",
  cdfFailures:
    "P(Y ≤ k) = 1 − (1 − p)^(k + 1) = 1 − (1 − {p})^({k} + 1) = {value}",
  rTrials:
    "In R: dgeom({y}, {p}) = {pmf} and pgeom({y}, {p}) = {cdf}. R counts failures before the first success, so a first success on trial {k} is {y} failures.",
  rFailures:
    "In R: dgeom({k}, {p}) = {pmf} and pgeom({k}, {p}) = {cdf}. R counts failures before the first success, which is the convention you chose, so the numbers carry straight over.",
  otherTrials:
    "Counting failures instead (Y = X − 1), the mean is {mean} and the variance is the same, {variance}. P(X = {k}) equals P(Y = {y}).",
  otherFailures:
    "Counting trials instead (X = Y + 1), the mean is {mean} and the variance is the same, {variance}. P(Y = {k}) equals P(X = {x}).",
  chartHeading: "Probability distribution",
  chartLabel:
    "Bar chart of the geometric probabilities for values from {from} to {to}, with the bars your question covers drawn darker",
  tableCaption: "Geometric probabilities for p = {p}, values {from} to {to}",
  tableLimit:
    "The table and chart are drawn while the distribution fits in about 200 rows (p of about 0.04 or more); for a smaller p use the probabilities above.",
  enterP: "Enter p greater than 0 and at most 1, such as 0.2.",
  enterK: "Enter k as a whole number of 0 or more.",
} as const;

type Strings = typeof GEOMETRIC_DISTRIBUTION_CALCULATOR_STRINGS;

const MAX_K = 1_000_000_000;

interface Inputs {
  p: number;
  k: number;
  k2: number | null;
}

function readInputs(
  pText: string,
  kText: string,
  k2Text: string
): Inputs | keyof Strings {
  const p = parseNumberInput(pText);
  if (p === null || p <= 0 || p > 1) {
    return "enterP";
  }
  const k = parseIntegerInput(kText);
  const hasSecond = k2Text.trim() !== "";
  const k2 = hasSecond ? parseIntegerInput(k2Text) : null;
  const valid = (value: number | null) =>
    value !== null && value >= 0 && value <= MAX_K;
  if (!valid(k) || (hasSecond && !valid(k2))) {
    return "enterK";
  }
  return { p, k: k as number, k2 };
}

function labelsFor(
  convention: GeometricConvention,
  strings: Strings
): MeaningLabels {
  const base = {
    probability: strings.probability,
    meaning: strings.meaning,
    value: strings.value,
  };
  if (convention === "trials") {
    return {
      ...base,
      equal: strings.trialsEqual,
      atMost: strings.trialsAtMost,
      lessThan: strings.trialsLessThan,
      atLeast: strings.trialsAtLeast,
      greaterThan: strings.trialsGreaterThan,
      between: strings.trialsBetween,
    };
  }
  return {
    ...base,
    equal: strings.failuresEqual,
    atMost: strings.failuresAtMost,
    lessThan: strings.failuresLessThan,
    atLeast: strings.failuresAtLeast,
    greaterThan: strings.failuresGreaterThan,
    between: strings.failuresBetween,
  };
}

function Working({
  inputs,
  convention,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  convention: GeometricConvention;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { p, k } = inputs;
  const four = (value: number) =>
    fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const trials = convention === "trials";
  const inSupport = k >= geometricFirst(convention);
  const y = toRFailures(k, convention);
  const values = { p: sig(p), k: fmt(k) };
  const r = {
    ...values,
    y: fmt(y),
    pmf: four(model.pmf(k)),
    cdf: four(model.cdf(k)),
  };
  const otherMean = trials ? (1 - p) / p : 1 / p;
  const other = {
    mean: sig(otherMean),
    variance: sig(model.variance),
    k: fmt(k),
    y: fmt(k - 1),
    x: fmt(k + 1),
  };
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      {inSupport ? (
        <>
          <p className="font-mono text-muted-foreground text-sm">
            {t(trials ? strings.pmfTrials : strings.pmfFailures, {
              ...values,
              value: four(model.pmf(k)),
            })}
          </p>
          <p className="font-mono text-muted-foreground text-sm">
            {t(trials ? strings.cdfTrials : strings.cdfFailures, {
              ...values,
              value: four(model.cdf(k)),
            })}
          </p>
        </>
      ) : null}
      {y >= 0 ? (
        <p className="text-muted-foreground text-sm">
          {t(trials ? strings.rTrials : strings.rFailures, r)}
        </p>
      ) : null}
      {inSupport ? (
        <p className="text-muted-foreground text-sm">
          {t(trials ? strings.otherTrials : strings.otherFailures, other)}
        </p>
      ) : null}
    </div>
  );
}

function Distribution({
  inputs,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { k, k2, p } = inputs;
  const range = useMemo(
    () => tableRange(model, k2 === null ? [k] : [k, k2]),
    [model, k, k2]
  );
  const rows = useMemo(
    () => (range ? distributionTable(model, range[0], range[1]) : []),
    [model, range]
  );
  const geometry = useMemo(
    () => barGeometry(rows.map((row) => ({ k: row.k, p: row.pmf }))),
    [rows]
  );
  if (!range) {
    return <Notice>{strings.tableLimit}</Notice>;
  }
  const low = k2 === null ? k : Math.min(k, k2);
  const high = k2 === null ? k : Math.max(k, k2);
  const highlighted = (value: number) => value >= low && value <= high;
  const span = { from: fmt(range[0]), to: fmt(range[1]) };
  return (
    <>
      <div className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
        <BarChart
          format={(value) => fmt(value)}
          geometry={geometry}
          highlighted={highlighted}
          label={t(strings.chartLabel, span)}
        />
      </div>
      <DistributionTable
        caption={t(strings.tableCaption, {
          ...span,
          p: fmt(p, { maximumSignificantDigits: 5 }),
        })}
        fmt={fmt}
        highlighted={highlighted}
        rows={rows}
      />
    </>
  );
}

export default function GeometricDistributionCalculatorWidget() {
  const strings = useToolUi(GEOMETRIC_DISTRIBUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [convention, setConvention] = useState<GeometricConvention>("trials");
  const [pText, setPText] = useState("0.2");
  const [kText, setKText] = useState("5");
  const [k2Text, setK2Text] = useState("");

  const inputs = readInputs(pText, kText, k2Text);
  const p = typeof inputs === "object" ? inputs.p : 0.5;
  const model = useMemo(() => geometricModel(p, convention), [p, convention]);
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const trials = convention === "trials";

  return (
    <>
      <section className="space-y-5">
        <PillGroup
          label={strings.conventionLabel}
          name="gd-convention"
          onChange={setConvention}
          options={[
            { value: "trials", label: strings.conventionTrials },
            { value: "failures", label: strings.conventionFailures },
          ]}
          value={convention}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="gd-p"
            label={strings.pLabel}
            numeric
            onChange={setPText}
            value={pText}
          />
          <TextField
            id="gd-k"
            label={trials ? strings.kTrialsLabel : strings.kFailuresLabel}
            numeric
            onChange={setKText}
            value={kText}
          />
          <TextField
            hint={strings.k2Hint}
            id="gd-k2"
            label={strings.k2Label}
            numeric
            onChange={setK2Text}
            value={k2Text}
          />
        </div>
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof inputs === "string" ? (
          <Notice>{strings[inputs]}</Notice>
        ) : (
          <>
            <ProbabilitySummary
              fmt={fmt}
              k={inputs.k}
              labels={labelsFor(convention, strings)}
              probabilities={discreteProbabilities(model, inputs.k, inputs.k2)}
            />
            <StatGrid>
              <StatCard
                label={trials ? strings.meanTrials : strings.meanFailures}
                value={sig(model.mean)}
              />
              <StatCard label={strings.variance} value={sig(model.variance)} />
              <StatCard
                label={strings.sd}
                value={sig(Math.sqrt(model.variance))}
              />
              <StatCard
                label={strings.median}
                value={fmt(geometricQuantile(0.5, inputs.p, convention))}
              />
            </StatGrid>
            <Working
              convention={convention}
              fmt={fmt}
              inputs={inputs}
              model={model}
              strings={strings}
            />
            <Distribution
              fmt={fmt}
              inputs={inputs}
              model={model}
              strings={strings}
            />
          </>
        )}
      </section>
    </>
  );
}
